import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import AllInboxRoundedIcon from "@mui/icons-material/AllInboxRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import NoteAltRoundedIcon from "@mui/icons-material/NoteAltRounded";
import QuestionAnswerRoundedIcon from "@mui/icons-material/QuestionAnswerRounded";
import { Badge, Box, Sheet } from "@mui/joy";
import { useColorScheme } from "@mui/joy/styles";
import { useNavigate } from "react-router-dom";

import { ChatManagementState } from "../../hooks/chats/useChatManagement";
import { useIsMobile } from "../../hooks/common/useIsMobile";
import { usePendingServiceHighlight } from "../../hooks/common/usePendingServiceHighlight";
import {
    HIDEABLE_SIDEBAR_ITEMS,
    SidebarItemKey,
    useSidebarVisibility,
} from "../../hooks/common/useSidebarVisibility";
import { InboxManagementState } from "../../hooks/inbox/useInboxManagement";
import { useTranslation } from "../../i18n";
import { switchService } from "../../services/serviceSwitch/serviceSwitchStore";
import { purplePalette } from "../../theme/purplePalette";

type BottomTabBarProps = {
    useIM: InboxManagementState;
    useCM: ChatManagementState;
    onOpenAccount: () => void;
};

// Mirrors NAV_ITEMS in `sidebar.tsx`. Kept inline rather than imported
// because the desktop sidebar is structurally different (vertical list
// with sub-affordances); we don't want changes there to leak into the
// mobile tab bar.
const TAB_ITEMS = [
    {
        id: 0,
        icon: AllInboxRoundedIcon,
        labelKey: "inbox" as const,
        path: "/workspace/inbox",
    },
    {
        id: 1,
        icon: QuestionAnswerRoundedIcon,
        labelKey: "chats" as const,
        path: "/workspace/chat",
    },
    {
        id: 2,
        icon: AssignmentRoundedIcon,
        labelKey: "tasks" as const,
        path: "/workspace/tasks",
    },
    {
        id: 3,
        icon: NoteAltRoundedIcon,
        labelKey: "notes" as const,
        path: "/workspace/notes",
    },
    // Account opens a sheet rather than navigating: settings, theme, team
    // switch and sign-out live in the desktop sidebar, which is `null` on
    // mobile. `path` is empty so nothing here is ever route-active.
    {
        id: 4,
        icon: AccountCircleRoundedIcon,
        labelKey: "account" as const,
        path: "",
    },
];

export const BottomTabBar = (props: BottomTabBarProps) => {
    const { useIM, useCM, onOpenAccount } = props;
    const isMobile = useIsMobile();
    const { mode } = useColorScheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    // What the bar lights up: the route, except between a tap and the switch
    // committing, when it is the tab just tapped (MIP-965).
    const { highlightPath, markPending } = usePendingServiceHighlight();
    const { isVisible } = useSidebarVisibility();

    if (!isMobile) return null;

    // Hide tabs the user turned off in Settings → Sidebar. Account has no
    // toggle (like Genos on desktop), so it's kept via the labelKey guard.
    const hideable = new Set<string>(HIDEABLE_SIDEBAR_ITEMS);
    const visibleTabs = TAB_ITEMS.filter(
        (item) => !hideable.has(item.labelKey) || isVisible(item.labelKey as SidebarItemKey)
    );

    const isDark = mode === "dark";
    const palette = isDark ? purplePalette.dark : purplePalette.light;
    const accent = isDark ? purplePalette.dark.accentSoft : purplePalette.light.accent;
    // Triplet companion — the palette is CSS variables, so alpha has to be
    // composed via rgba() rather than appended as hex.
    const accentRgb = isDark ? purplePalette.dark.accentSoftRgb : purplePalette.light.accentRgb;

    const getBadgeCount = (id: number): number => {
        if (id === 0) return useIM.unReadInboxItemCount;
        if (id === 1) return useCM.unReadChatAndActivityCounts;
        return 0;
    };

    return (
        <Sheet
            sx={{
                position: "fixed",
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1200,
                // The variable is the bar's TOTAL footprint (60px of tabs +
                // the home-indicator strip). `box-sizing: border-box` means
                // the padding below comes out of this height, leaving the
                // tabs their full 60px — before, the inset was subtracted
                // from 60 and the icons had ~26px on a notched iPhone.
                height: "var(--BottomTabBar-height, 60px)",
                display: "flex",
                alignItems: "stretch",
                justifyContent: "space-around",
                borderTop: "1px solid",
                borderColor: palette.divider,
                background: isDark
                    ? "linear-gradient(180deg, rgba(var(--gp-dark-surface-b-rgb), 0.96) 0%, rgba(11,10,22,0.98) 100%)"
                    : "linear-gradient(180deg, rgba(252,250,255,0.96) 0%, rgba(248,245,255,0.98) 100%)",
                backdropFilter: "blur(12px)",
                paddingBottom: "env(safe-area-inset-bottom, 0)",
            }}
        >
            {visibleTabs.map((item) => {
                const Icon = item.icon;
                // Guard on empty `path` — `includes("")` is true for every
                // route, which would light up the Account tab everywhere.
                const isActive = item.path !== "" && highlightPath.includes(item.path);
                const badgeCount = getBadgeCount(item.id);

                return (
                    <Box
                        key={item.id}
                        sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.25,
                            cursor: "pointer",
                            color: isActive
                                ? accent
                                : isDark
                                  ? "rgba(255,255,255,0.55)"
                                  : "rgba(0,0,0,0.5)",
                            transition: "color 0.2s ease",
                            "&:active": { opacity: 0.7 },
                        }}
                        onClick={() => {
                            if (item.path === "") {
                                onOpenAccount();
                                return;
                            }
                            markPending(item.path);
                            switchService(navigate, item.path);
                        }}
                    >
                        <Badge
                            badgeContent={badgeCount > 0 ? badgeCount : 0}
                            invisible={badgeCount === 0}
                            size="sm"
                            sx={{
                                "& .MuiBadge-badge": {
                                    background: `linear-gradient(135deg, ${accent} 0%, rgba(${accentRgb}, 0.8) 100%)`,
                                    color: "#fff",
                                    fontWeight: 700,
                                    fontSize: "0.6rem",
                                    minWidth: 18,
                                    height: 18,
                                },
                            }}
                        >
                            <Icon sx={{ fontSize: 22 }} />
                        </Badge>
                        <Box
                            sx={{
                                fontSize: "0.62rem",
                                fontWeight: isActive ? 600 : 500,
                                letterSpacing: "0.02em",
                            }}
                        >
                            {t.sidebar.nav[item.labelKey]}
                        </Box>
                    </Box>
                );
            })}
        </Sheet>
    );
};
